import React from "react";
import {
  Card,
  CardBody,
  Divider,
  Accordion,
  AccordionItem,
} from "@heroui/react";
import { Info, FileText, Upload, Code, Database } from "@phosphor-icons/react";
import DynamicPlaceholderBrowser from "./DynamicPlaceholderBrowser";

const DocumentTemplateInstructions: React.FC = () => {
  return (
    <div className="space-y-4">
      <Card className="border border-blue-200 bg-blue-50">
        <CardBody className="p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <Info size={24} className="text-blue-600" />
            </div>
            <div className="space-y-4">
              <div>
                <h3 className="mb-2 text-lg font-semibold text-blue-900">
                  Hoe maak je een Word document sjabloon?
                </h3>
                <p className="mb-4 text-blue-800">
                  Word document sjablonen zijn complexer dan e-mail sjablonen en
                  kunnen niet via een teksteditor worden gemaakt. Volg deze
                  stappen om een professioneel document sjabloon te maken:
                </p>
              </div>

              <div className="space-y-6">
                <div className="grid gap-6 md:grid-cols-2">
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 font-medium text-blue-900">
                      <FileText size={20} />
                      <span>1. Maak het document in Word</span>
                    </div>
                    <ul className="ml-6 list-inside list-disc space-y-1 text-blue-800">
                      <li>Open Microsoft Word</li>
                      <li>Ontwerp je document met alle gewenste opmaak</li>
                      <li>Voeg tabellen, headers, footers toe zoals gewenst</li>
                      <li>Gebruik professionele styling en layout</li>
                    </ul>

                    <div className="flex items-center gap-2 font-medium text-blue-900">
                      <Code size={20} />
                      <span>2. Voeg automatisch invulbare velden toe</span>
                    </div>
                    <ul className="ml-6 list-inside list-disc space-y-1 text-blue-800">
                      <li>
                        Vervang vaste tekst door velden die automatisch worden
                        ingevuld
                      </li>
                      <li>
                        Type tussen accolades {} wat je wilt tonen:{" "}
                        <code className="rounded bg-blue-100 px-1">
                          {"{tabel.kolomnaam}"}
                        </code>
                      </li>
                      <li>
                        Voorbeeld:{" "}
                        <code className="rounded bg-blue-100 px-1">
                          {"{contact.firstName}"}
                        </code>{" "}
                        wordt de voornaam van de huurder
                      </li>
                      <li>
                        Voorbeeld:{" "}
                        <code className="rounded bg-blue-100 px-1">
                          {"{unit.id}"}
                        </code>{" "}
                        wordt het unit nummer
                      </li>
                      <li>
                        Voor gekoppelde gegevens:{" "}
                        <code className="rounded bg-blue-100 px-1">
                          {"{unit.building.address}"}
                        </code>{" "}
                        haalt het adres op van het gebouw waar de unit in staat
                      </li>
                      <li>
                        Voor eigen in te vullen tekst:{" "}
                        <code className="rounded bg-blue-100 px-1">
                          {"{waarde.datumVandaag}"}
                        </code>{" "}
                        laat je later bij het genereren zelf een waarde invullen
                      </li>
                    </ul>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center gap-2 font-medium text-blue-900">
                      <Upload size={20} />
                      <span>3. Automatische upload & validatie</span>
                    </div>
                    <ul className="ml-6 list-inside list-disc space-y-1 text-blue-800">
                      <li>Sla het document op als .docx bestand</li>
                      <li>Selecteer het juiste document type in het systeem</li>
                      <li>Upload wordt automatisch naar SharePoint gedaan</li>
                      <li>Placeholders worden automatisch gevalideerd</li>
                    </ul>

                    <div className="rounded-lg bg-blue-100 p-3">
                      <p className="text-sm text-blue-800">
                        <strong>Tip:</strong> Test je sjabloon eerst met enkele
                        placeholders voordat je alle velden toevoegt.
                      </p>
                    </div>
                  </div>
                </div>

                <Divider />

                <div className="space-y-4">
                  <div className="mb-3 flex items-center gap-2 font-medium text-blue-900">
                    <Code size={20} />
                    <span>
                      Geavanceerde Functionaliteit - Praktische Voorbeelden
                    </span>
                  </div>

                  <p className="mb-4 text-sm text-blue-800">
                    Klik op een voorbeeld om de exacte code te zien die je in je
                    Word document moet typen. Je kunt deze voorbeelden kopiëren
                    en direct gebruiken:
                  </p>

                  <Accordion variant="splitted" className="px-0">
                    {/* FOR LOOP - Units overlopen */}
                    <AccordionItem
                      key="units-loop"
                      aria-label="De units van een gebouw overlopen"
                      title="De units van een gebouw overlopen"
                      classNames={{
                        base: "bg-blue-50 border border-blue-200 shadow-sm",
                        title: "text-blue-900 font-medium text-sm",
                        content: "text-blue-800 pb-4",
                      }}
                    >
                      <div className="space-y-3">
                        <p className="text-sm">
                          Gebruik dit wanneer je alle units van een gebouw wilt
                          tonen, bijvoorbeeld in een overzichtsrapport:
                        </p>
                        <code className="block rounded bg-blue-100 p-3 font-mono text-xs whitespace-pre">
                          {"[[for unit in building.units]]"}
                          {"\n"}
                          {"Unit nummer: {unit.boxNumber}"}
                          {"\n"}
                          {"Type: {unit.unitType}"}
                          {"\n"}
                          {"Oppervlakte: {unit.surface} m²"}
                          {"\n"}
                          {"Huurprijs: € {unit.rent}"}
                          {"\n"}
                          {""}
                          {"\n"}
                          {"[[endfor]]"}
                        </code>
                        <p className="rounded bg-blue-100 p-2 text-xs text-blue-700">
                          💡 <strong>Tip:</strong> Je kunt alle unit
                          eigenschappen gebruiken zoals boxNumber, unitType,
                          surface, rent, furnished, etc.
                        </p>
                      </div>
                    </AccordionItem>

                    {/* FOR LOOP - Huurders overlopen */}
                    <AccordionItem
                      key="tenants-loop"
                      aria-label="Alle huurders van een gebouw tonen"
                      title="Alle huurders van een gebouw tonen"
                      classNames={{
                        base: "bg-blue-50 border border-blue-200 shadow-sm",
                        title: "text-blue-900 font-medium text-sm",
                        content: "text-blue-800 pb-4",
                      }}
                    >
                      <div className="space-y-3">
                        <p className="text-sm">
                          Gebruik dit om een lijst van alle huurders in een
                          gebouw te maken:
                        </p>
                        <code className="block rounded bg-blue-100 p-3 font-mono text-xs whitespace-pre">
                          {"[[for unit in building.units]]"}
                          {"\n"}
                          {"  [[for tenant in unit.tenants]]"}
                          {"\n"}
                          {"  Huurder: {contact.firstName} {tenant.lastName}"}
                          {"\n"}
                          {"  Unit: {unit.boxNumber}"}
                          {"\n"}
                          {"  Email: {tenant.email}"}
                          {"\n"}
                          {"  Telefoon: {tenant.phone}"}
                          {"\n"}
                          {""}
                          {"\n"}
                          {"  [[endfor]]"}
                          {"\n"}
                          {"[[endfor]]"}
                        </code>
                        <p className="rounded bg-blue-100 p-2 text-xs text-blue-700">
                          💡 <strong>Tip:</strong> Dit is een geneste loop -
                          eerst overlopen we units, dan per unit de huurders.
                        </p>
                      </div>
                    </AccordionItem>

                    {/* IF STATEMENT - Gemeubileerd */}
                    <AccordionItem
                      key="if-furnished"
                      aria-label="Toon tekst als unit gemeubileerd is"
                      title="Toon tekst als unit gemeubileerd is"
                      classNames={{
                        base: "bg-green-50 border border-green-200 shadow-sm",
                        title: "text-green-900 font-medium text-sm",
                        content: "text-green-800 pb-4",
                      }}
                    >
                      <div className="space-y-3">
                        <p className="text-sm">
                          Gebruik dit om verschillende tekst te tonen
                          afhankelijk van of een unit gemeubileerd is:
                        </p>
                        <code className="block rounded bg-green-100 p-3 font-mono text-xs whitespace-pre">
                          {"[[if unit.furnished]]"}
                          {"\n"}
                          {
                            "Deze unit is volledig gemeubileerd en direct bewoonbaar."
                          }
                          {"\n"}
                          {"[[else]]"}
                          {"\n"}
                          {"Deze unit is niet gemeubileerd."}
                          {"\n"}
                          {"[[endif]]"}
                        </code>
                        <p className="rounded bg-green-100 p-2 text-xs text-green-700">
                          💡 <strong>Tip:</strong> Het [[else]] gedeelte is
                          optioneel. Je kunt het weglaten als je alleen tekst
                          wilt tonen wanneer de voorwaarde waar is.
                        </p>
                      </div>
                    </AccordionItem>

                    {/* IF STATEMENT - Prijs vergelijking */}
                    <AccordionItem
                      key="if-price"
                      aria-label="Toon tekst op basis van huurprijs"
                      title="Toon tekst op basis van huurprijs"
                      classNames={{
                        base: "bg-green-50 border border-green-200 shadow-sm",
                        title: "text-green-900 font-medium text-sm",
                        content: "text-green-800 pb-4",
                      }}
                    >
                      <div className="space-y-3">
                        <p className="text-sm">
                          Gebruik dit om verschillende tekst te tonen op basis
                          van de huurprijs:
                        </p>
                        <code className="block rounded bg-green-100 p-3 font-mono text-xs whitespace-pre">
                          {"[[if unit.rent > 1000]]"}
                          {"\n"}
                          {"Premium unit met luxe voorzieningen"}
                          {"\n"}
                          {"[[elseif unit.rent > 500]]"}
                          {"\n"}
                          {"Standaard unit"}
                          {"\n"}
                          {"[[else]]"}
                          {"\n"}
                          {"Budget vriendelijke unit"}
                          {"\n"}
                          {"[[endif]]"}
                        </code>
                        <p className="rounded bg-green-100 p-2 text-xs text-green-700">
                          💡 <strong>Ondersteunde operatoren:</strong> ==
                          (gelijk aan), != (niet gelijk aan), {">"} (groter
                          dan), {"<"} (kleiner dan), {">="} (groter of gelijk),{" "}
                          {"<="} (kleiner of gelijk)
                        </p>
                      </div>
                    </AccordionItem>

                    {/* COMBINATION - Loop met condities */}
                    <AccordionItem
                      key="loop-with-conditions"
                      aria-label="Units tonen met specifieke voorwaarden"
                      title="Units tonen met specifieke voorwaarden (combinatie)"
                      classNames={{
                        base: "bg-amber-50 border border-amber-200 shadow-sm",
                        title: "text-amber-900 font-medium text-sm",
                        content: "text-amber-800 pb-4",
                      }}
                    >
                      <div className="space-y-3">
                        <p className="text-sm">
                          Combineer loops en condities om specifieke units te
                          filteren:
                        </p>
                        <code className="block rounded bg-amber-100 p-3 font-mono text-xs whitespace-pre">
                          {"[[for unit in building.units]]"}
                          {"\n"}
                          {"  [[if unit.rent > 800 && unit.furnished]]"}
                          {"\n"}
                          {""}
                          {"\n"}
                          {"  Premium gemeubileerde unit:"}
                          {"\n"}
                          {"  - Unit nummer: {unit.boxNumber}"}
                          {"\n"}
                          {"  - Type: {unit.unitType}"}
                          {"\n"}
                          {"  - Huurprijs: € {unit.rent}"}
                          {"\n"}
                          {"  - Status: Gemeubileerd ✓"}
                          {"\n"}
                          {""}
                          {"\n"}
                          {"  [[endif]]"}
                          {"\n"}
                          {"[[endfor]]"}
                        </code>
                        <p className="rounded bg-amber-100 p-2 text-xs text-amber-700">
                          💡 <strong>Tip:</strong> Gebruik && voor "EN" en ||
                          voor "OF" om meerdere voorwaarden te combineren.
                        </p>
                      </div>
                    </AccordionItem>

                    {/* IMAGES */}
                    <AccordionItem
                      key="images"
                      aria-label="Voeg foto's toe aan het document"
                      title="Voeg foto's toe aan het document"
                      classNames={{
                        base: "bg-purple-50 border border-purple-200 shadow-sm",
                        title: "text-purple-900 font-medium text-sm",
                        content: "text-purple-800 pb-4",
                      }}
                    >
                      <div className="space-y-3">
                        <p className="text-sm">
                          Voeg placeholders toe voor afbeeldingen die je later
                          kunt selecteren uit SharePoint:
                        </p>
                        <code className="block rounded bg-purple-100 p-3 font-mono text-xs whitespace-pre">
                          {"Gebouw exterieur:"}
                          {"\n"}
                          {"{%image1}"}
                          {"\n"}
                          {""}
                          {"\n"}
                          {"Unit interieur:"}
                          {"\n"}
                          {"{%image2}"}
                          {"\n"}
                          {""}
                          {"\n"}
                          {"Plattegrond:"}
                          {"\n"}
                          {"{%image3}"}
                        </code>
                        <p className="rounded bg-purple-100 p-2 text-xs text-purple-700">
                          💡 <strong>Tip:</strong> Gebruik {"{%image1}"},{" "}
                          {"{%image2}"}, {"{%image3}"}, etc. voor zoveel
                          afbeeldingen als je nodig hebt. Bij het genereren kun
                          je voor elk nummer een foto selecteren.
                        </p>
                      </div>
                    </AccordionItem>

                    {/* DYNAMIC VALUES AND IMAGES IN LOOPS */}
                    <AccordionItem
                      key="dynamic-loop-values"
                      aria-label="Foto's en eigen waarden toevoegen per unit"
                      title="Foto's en eigen waarden toevoegen per unit (in loops)"
                      classNames={{
                        base: "bg-rose-50 border border-rose-200 shadow-sm",
                        title: "text-rose-900 font-medium text-sm",
                        content: "text-rose-800 pb-4",
                      }}
                    >
                      <div className="space-y-3">
                        <p className="text-sm">
                          Wanneer je door units loopt, kun je voor elke unit een
                          eigen foto of waarde toevoegen die je later invult bij
                          het genereren:
                        </p>
                        <code className="block rounded bg-rose-100 p-3 font-mono text-xs whitespace-pre">
                          {"[[for currentUnit in building.units]]"}
                          {"\n"}
                          {""}
                          {"\n"}
                          {"═══════════════════════════════"}
                          {"\n"}
                          {"Unit: {currentUnit.boxNumber}"}
                          {"\n"}
                          {"Type: {currentUnit.unitType}"}
                          {"\n"}
                          {"Huurprijs: € {currentUnit.rent}"}
                          {"\n"}
                          {""}
                          {"\n"}
                          {"Foto van deze unit:"}
                          {"\n"}
                          {"{currentUnit.%image}"}
                          {"\n"}
                          {""}
                          {"\n"}
                          {"Bijzonderheden:"}
                          {"\n"}
                          {"{currentUnit.waarde.bijzonderheden}"}
                          {"\n"}
                          {""}
                          {"\n"}
                          {"Opmerkingen:"}
                          {"\n"}
                          {"{currentUnit.waarde.opmerkingen}"}
                          {"\n"}
                          {"═══════════════════════════════"}
                          {"\n"}
                          {""}
                          {"\n"}
                          {"[[endfor]]"}
                        </code>
                        <div className="space-y-2">
                          <p className="rounded bg-rose-100 p-2 text-xs text-rose-700">
                            💡 <strong>Foto per unit:</strong> Gebruik{" "}
                            <code>{"{currentUnit.%image}"}</code> om bij elke
                            unit een eigen foto te kunnen selecteren. Je kunt
                            meerdere foto's toevoegen met{" "}
                            <code>{"{currentUnit.%image2}"}</code>,{" "}
                            <code>{"{currentUnit.%image3}"}</code>, etc.
                          </p>
                          <p className="rounded bg-rose-100 p-2 text-xs text-rose-700">
                            💡 <strong>Eigen waarde per unit:</strong> Gebruik{" "}
                            <code>{"{currentUnit.waarde.naam}"}</code> waarbij
                            "naam" een vrijgekozen naam is. Bij het genereren
                            kun je dan voor elke unit een eigen tekst invullen.
                          </p>
                          <p className="rounded bg-rose-100 p-2 text-xs text-rose-700">
                            ⚠️ <strong>Let op:</strong> "currentUnit" is hier de
                            naam van de loop variabele. Als je een andere naam
                            gebruikt (bijvoorbeeld "unit"), dan moet je ook{" "}
                            <code>{"{unit.%image}"}</code> of{" "}
                            <code>{"{unit.waarde.naam}"}</code> gebruiken.
                          </p>
                        </div>
                      </div>
                    </AccordionItem>
                  </Accordion>

                  <div className="mt-4 rounded-lg bg-blue-100 p-4">
                    <p className="text-sm text-blue-800">
                      <strong>💡 Pro tip:</strong> Kopieer de code voorbeelden
                      hierboven en plak ze direct in je Word document. Test je
                      sjabloon met enkele voorbeelden voordat je complexere
                      structuren toevoegt.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="p-6">
          <div className="mb-4 flex items-center gap-2">
            <Database size={20} className="text-primary" />
            <h4 className="text-neutral-primary text-lg font-semibold">
              Beschikbare Placeholders (Live Database)
            </h4>
          </div>
          <p className="text-neutral-secondaryMid mb-4">
            Hieronder zie je alle beschikbare placeholders uit je live database.
            Klik op een kolom om de placeholder te kopiëren:
          </p>

          <DynamicPlaceholderBrowser />

          <div className="mt-4 rounded-lg border border-yellow-200 bg-yellow-50 p-4">
            <p className="text-sm text-yellow-800">
              <strong>Let op:</strong> Gebruik de tabel.kolom syntax (bijv.{" "}
              <code>{"{contact.firstName}"}</code>). Het systeem valideert
              automatisch placeholders tegen de live database schema.
            </p>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default DocumentTemplateInstructions;
